// Deliberately free of React and API imports so the save outcome can be unit-tested directly.

export type UserSavePart = 'details' | 'account settings' | 'groups' | 'locations'

// Named in the order the modal lays them out, whatever order the requests settled in.
const PART_ORDER: UserSavePart[] = ['details', 'account settings', 'groups', 'locations']

export interface UserSaveResult {
  part: UserSavePart
  result: PromiseSettledResult<unknown>
}

export type UserSaveOutcome =
  | { kind: 'success' }
  | {
    kind: 'error'
    failedParts: UserSavePart[]
    message: string
  }

const joinParts = (parts: string[]) =>
  parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

// One save sends up to seven requests across four parts. The user gets one confirmation or
// one error for the whole action — never a success alongside a failure.
export const summariseSave = (
  results: UserSaveResult[],
  getErrorMessage: (error: unknown) => string | undefined,
): UserSaveOutcome => {
  const failures = results.flatMap(({ part, result }) =>
    result.status === 'rejected'
      ? [{
        part,
        reason: getErrorMessage(result.reason),
      }]
      : [],
  )

  if (failures.length === 0) return { kind: 'success' }

  const failedParts = PART_ORDER.filter((part) => failures.some((failure) => failure.part === part))

  const summary = joinParts(failedParts)
  // Every part name is plural ('details', 'groups', …), so it is always 'were'.
  const notSaved = `${summary.charAt(0).toUpperCase()}${summary.slice(1)} were not saved.`

  // When every failure has the same cause — typically one 403 across the board — say it once.
  // Different causes are not listed: the parts that failed are what the user acts on.
  const reasons = new Set(failures.map((failure) => failure.reason))
  const [sharedReason] = reasons

  return {
    kind: 'error',
    failedParts,
    message: reasons.size === 1 && sharedReason ? `${notSaved} ${sharedReason}` : notSaved,
  }
}
